import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { formatCents } from '@/lib/format/currency';
import {
  formatAccountDisqualificationErrorValues,
  mapAccountDisqualificationError,
} from '@/lib/campaigns/accountDisqualification';
import {
  ADMIN_CPM_ACCOUNT_DISQUALIFICATION_PREVIEW,
  ADMIN_DISQUALIFY_CPM_CAMPAIGN_ACCOUNT,
} from '@/queries/accountDisqualification';
import { ADMIN_DISQUALIFY_CPM_CAMPAIGN_USER } from '@/queries/queries';
import type { CpmAccountDisqualificationPreview } from '@/generated/account-disqualification';

/** Which ban a confirmed dialog applied, so the page can refresh accordingly. */
export type DisqualifyScope = 'account' | 'user';

type Props = {
  submissionId: string | null;
  onOpenChange: (open: boolean) => void;
  onDone: (scope: DisqualifyScope) => void;
  /** Campaign currency for the money lines (CPM only). */
  currency?: string;
  /**
   * The clip's owner. When given, the dialog offers to disqualify every
   * account of this user (the existing user-level disqualification).
   */
  user?: { campaignId: string; userId: string; label?: string | null } | null;
};

const PREFIX = 'admin.campaigns.accountDisqualification';

function DisqualifyAccountBody({ submissionId, onOpenChange, onDone, currency = 'BRL', user }: Props & { submissionId: string }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [reason, setReason] = useState('');
  const [allAccounts, setAllAccounts] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { data, loading, error } = useQuery<{ adminCpmAccountDisqualificationPreview: CpmAccountDisqualificationPreview }>(
    ADMIN_CPM_ACCOUNT_DISQUALIFICATION_PREVIEW,
    { variables: { submissionId }, fetchPolicy: 'no-cache' },
  );
  const [disqualifyAccount, { loading: accountPending }] = useMutation(ADMIN_DISQUALIFY_CPM_CAMPAIGN_ACCOUNT);
  const [disqualifyUser, { loading: userPending }] = useMutation(ADMIN_DISQUALIFY_CPM_CAMPAIGN_USER);
  const pending = accountPending || userPending;
  const preview = data?.adminCpmAccountDisqualificationPreview;
  const isCpm = !!preview && preview.campaign_type !== 'ranking';
  const wholeUser = allAccounts && !!user;
  const userLabel = user?.label || user?.userId || '';
  // Re-banning an active account updates its reason and adds the clips that still count.
  const updating = !wholeUser && !!preview?.already_active;
  const platform = preview
    ? t(`campaign.channels.platformLabel.${preview.platform}`, { defaultValue: preview.platform })
    : '';

  const confirm = async () => {
    if (!reason.trim() || pending) return;
    try {
      if (wholeUser && user) {
        await disqualifyUser({ variables: { campaignId: user.campaignId, userId: user.userId, reason: reason.trim() } });
        toast({ title: t(`${PREFIX}.successUserDisqualified`) });
      } else {
        await disqualifyAccount({ variables: { submissionId, reason: reason.trim() } });
        toast({ title: t(`${PREFIX}.${updating ? 'successUpdated' : 'successDisqualified'}`) });
      }
      onOpenChange(false);
      onDone(wholeUser ? 'user' : 'account');
    } catch (err) {
      const { key, values } = mapAccountDisqualificationError(err);
      setErrorMessage(t(key, formatAccountDisqualificationErrorValues(values, currency)));
    }
  };

  let titleKey = 'disqualifyTitle';
  if (wholeUser) titleKey = 'allAccountsTitle';
  else if (updating) titleKey = 'updateTitle';
  let descriptionKey = 'disqualifyDescription';
  if (wholeUser) descriptionKey = 'allAccountsDescription';
  else if (updating) descriptionKey = 'updateDescription';
  let confirmKey = 'confirmDisqualify';
  if (wholeUser) confirmKey = 'confirmDisqualifyUser';
  else if (updating) confirmKey = 'confirmUpdate';
  const titleValues = { account: preview?.account_label ?? '', platform, user: userLabel };

  return (
    <>
      <Dialog open onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t(`${PREFIX}.${titleKey}`, titleValues)}</DialogTitle>
            <DialogDescription>{t(`${PREFIX}.${descriptionKey}`, titleValues)}</DialogDescription>
          </DialogHeader>
          {!wholeUser && loading && <Loader2 aria-hidden="true" className="mx-auto h-5 w-5 animate-spin" />}
          {!wholeUser && error && <p role="alert" className="text-sm text-destructive">{t(`${PREFIX}.previewLoadError`)}</p>}
          {!wholeUser && preview && (
            <div className="space-y-1 rounded-md border bg-muted/20 p-3 text-sm">
              <p>{t(`${PREFIX}.previewAffected`, { count: preview.affected_count })}</p>
              {isCpm && <>
                <p>{t(`${PREFIX}.previewReversed`, { amount: formatCents(preview.reversed_cents, currency) })}</p>
                <p>{t(`${PREFIX}.previewRefunded`, { amount: formatCents(preview.refunded_cents, currency) })}</p>
                <p>{t(`${PREFIX}.previewRetained`, { amount: formatCents(preview.retained_cents, currency) })}</p>
              </>}
            </div>
          )}
          {wholeUser && (
            <p className="rounded-md border bg-muted/20 p-3 text-sm">
              {t(`${PREFIX}.${preview?.campaign_type === 'ranking' ? 'allAccountsNoteRanking' : 'allAccountsNote'}`)}
            </p>
          )}
          {user && (
            <div className="flex items-start gap-2">
              <Checkbox
                id="account-dq-all-accounts"
                data-testid="account-dq-all-accounts"
                checked={allAccounts}
                disabled={pending}
                onCheckedChange={value => setAllAccounts(value === true)}
              />
              <Label htmlFor="account-dq-all-accounts" className="text-sm font-normal leading-snug">
                {t(`${PREFIX}.allAccountsLabel`, { user: userLabel })}
              </Label>
            </div>
          )}
          <div className="space-y-1">
            <Label htmlFor="account-dq-reason">{t(`${PREFIX}.reasonLabel`)}</Label>
            <Textarea
              id="account-dq-reason"
              rows={3}
              maxLength={2000}
              value={reason}
              disabled={pending}
              placeholder={t(`${PREFIX}.${wholeUser ? 'reasonPlaceholderUser' : 'reasonPlaceholder'}`)}
              onChange={event => setReason(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">{t(`${PREFIX}.reasonHint`)}</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" disabled={pending} onClick={() => onOpenChange(false)}>{t(`${PREFIX}.cancel`)}</Button>
            <Button
              variant="destructive"
              // The user-level ban needs no account preview.
              disabled={!reason.trim() || pending || (!wholeUser && !preview)}
              onClick={() => void confirm()}
            >
              {pending && <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />}
              {t(`${PREFIX}.${confirmKey}`)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!errorMessage} onOpenChange={() => setErrorMessage(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t(`${PREFIX}.action`)}</DialogTitle>
            <DialogDescription>{errorMessage}</DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function DisqualifyAccountDialog(props: Props) {
  // Keyed by submission so a reason or error never leaks between clips.
  return props.submissionId ? <DisqualifyAccountBody key={props.submissionId} {...props} submissionId={props.submissionId} /> : null;
}
